import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownRenderer = ({ content, className = '', style = {} }) => {
  const defaultStyle = {
    lineHeight: '1.6',
    color: '#374151',
    fontSize: '14px',
    ...style
  };

  const markdownComponents = {
    // Headings
    h1: ({ children }) => (
      <h1 style={{ 
        fontSize: '24px', 
        fontWeight: 'bold', 
        marginBottom: '16px', 
        marginTop: '24px',
        color: '#1f2937',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '8px'
      }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ 
        fontSize: '20px', 
        fontWeight: 'bold', 
        marginBottom: '12px', 
        marginTop: '20px',
        color: '#1f2937'
      }}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ 
        fontSize: '18px', 
        fontWeight: 'bold', 
        marginBottom: '10px', 
        marginTop: '16px',
        color: '#374151'
      }}>
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 style={{ 
        fontSize: '16px', 
        fontWeight: 'bold', 
        marginBottom: '8px', 
        marginTop: '14px',
        color: '#374151'
      }}>
        {children}
      </h4>
    ),
    
    // Paragraphs
    p: ({ children }) => (
      <p style={{ 
        marginBottom: '12px',
        lineHeight: '1.6'
      }}>
        {children}
      </p>
    ),
    
    // Lists
    ul: ({ children }) => (
      <ul style={{ 
        marginBottom: '12px', 
        paddingLeft: '20px',
        listStyleType: 'disc'
      }}>
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol style={{ 
        marginBottom: '12px', 
        paddingLeft: '20px',
        listStyleType: 'decimal'
      }}>
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li style={{ 
        marginBottom: '4px',
        lineHeight: '1.5'
      }}>
        {children}
      </li>
    ),
    
    // Code blocks
    code: ({ children, className }) => {
      const isInline = !className;
      
      if (isInline) {
        return (
          <code style={{
            backgroundColor: '#f3f4f6',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '13px',
            fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            color: '#e53e3e'
          }}>
            {children}
          </code>
        );
      }
      
      return (
        <pre style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '12px',
          overflow: 'auto',
          fontSize: '13px',
          fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          lineHeight: '1.4'
        }}>
          <code>{children}</code>
        </pre>
      );
    },
    
    // Blockquotes
    blockquote: ({ children }) => (
      <blockquote style={{
        borderLeft: '4px solid #3b82f6',
        paddingLeft: '16px',
        marginBottom: '12px',
        fontStyle: 'italic',
        color: '#6b7280',
        backgroundColor: '#f8fafc',
        padding: '12px 16px',
        borderRadius: '0 6px 6px 0'
      }}>
        {children}
      </blockquote>
    ),
    
    // Tables
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: '1px solid #e5e7eb',
          fontSize: '13px'
        }}>
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th style={{
        border: '1px solid #e5e7eb',
        padding: '8px 12px',
        backgroundColor: '#f9fafb',
        fontWeight: 'bold',
        textAlign: 'left'
      }}>
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td style={{
        border: '1px solid #e5e7eb',
        padding: '8px 12px'
      }}>
        {children}
      </td>
    ),
    
    // Links
    a: ({ children, href }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#3b82f6',
          textDecoration: 'underline',
          cursor: 'pointer'
        }}
      >
        {children}
      </a>
    ),
    
    // Strong and emphasis
    strong: ({ children }) => (
      <strong style={{ fontWeight: 'bold', color: '#1f2937' }}>
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em style={{ fontStyle: 'italic' }}>
        {children}
      </em>
    ),
    
    // Horizontal rule
    hr: () => (
      <hr style={{
        border: 'none',
        borderTop: '1px solid #e5e7eb',
        marginTop: '20px',
        marginBottom: '20px'
      }} />
    )
  };

  return (
    <div className={className} style={defaultStyle}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
